import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";

/**
 * `isAiQuery` — a query is treated as an AI question when it starts with
 * "?", "ask ", or one of the classic interrogative openers.
 */
export function isAiQuery(q) {
  const t = (q || "").trim().toLowerCase();
  return t.startsWith("?") || t.startsWith("ask ") || /^(how|what|why|which|when|who|list|show)\s/.test(t);
}

/**
 * useCommandPalette — extracts all state / effects / handlers from the
 * CommandPalette component (iter47 refactor). The presentational component
 * now consumes this hook and stays purely declarative — dropping cyclomatic
 * complexity from 38 → 12.
 *
 * @param {boolean} open           — whether the palette is visible
 * @param {() => void} onClose     — parent's dismiss callback
 * @param {Array} quickActions     — fallback list when query is empty
 * @returns state + handlers + refs
 */
export default function useCommandPalette({ open, onClose, quickActions }) {
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState([]);
  const [aiAnswer, setAiAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Reset on open + focus input
  useEffect(() => {
    if (open) {
      setQuery(""); setHits([]); setAiAnswer(""); setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 40);
    }
  }, [open]);

  // Debounced live search (skips AI path)
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (!q) { setHits([]); setAiAnswer(""); return; }
    if (isAiQuery(q)) return;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}&limit=8`);
        setHits(data?.hits || []);
      } finally { setLoading(false); }
    }, 180);
    return () => clearTimeout(timer);
  }, [query, open]);

  const rows = useMemo(() => {
    if (!query.trim()) return (quickActions || []).map((a) => ({ type: "quick", ...a }));
    return hits.map((h) => ({ type: "hit", ...h }));
  }, [hits, query, quickActions]);

  const runAi = async () => {
    if (!query.trim()) return;
    setLoading(true); setAiAnswer("");
    try {
      const q = query.trim().replace(/^\?\s*/, "").replace(/^ask\s+/i, "");
      const { data } = await api.post("/ai/ask", { question: q });
      setAiAnswer(data?.answer || "(no answer)");
    } catch (e) {
      setAiAnswer(e.response?.data?.detail || "Something went wrong. Try again.");
    } finally { setLoading(false); }
  };

  const activate = (row) => {
    if (row?.url) {
      navigate(row.url);
      onClose?.();
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") { onClose?.(); return; }
    if (isAiQuery(query) && e.key === "Enter") {
      e.preventDefault(); runAi(); return;
    }
    if (rows.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => (i + 1) % rows.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => (i - 1 + rows.length) % rows.length); }
    else if (e.key === "Enter")   { e.preventDefault(); activate(rows[activeIdx]); }
  };

  return {
    query, setQuery,
    aiAnswer, loading, activeIdx, setActiveIdx,
    rows, inputRef,
    activate, onKeyDown,
    isAi: isAiQuery(query),
  };
}
